/**
 * Login dedicado para operadores de Check-in.
 * URL: /admin/cursos/checkin/login
 *
 * Após login bem-sucedido, redirecciona para `/admin/cursos/checkin`.
 * Cookie próprio (`ihtp_checkin_session`), TTL 14 dias.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ScanBarcode } from "lucide-react";

// URL relativa para garantir same-origin em qualquer ambiente (preview,
// produção, custom domain). Crucial para iOS Safari, que bloqueia cookies
// `SameSite=None` em pedidos cross-origin — sintoma típico: o login "passa"
// mas o `/me` seguinte falha porque o cookie não é enviado, e o utilizador
// fica num loop a voltar ao ecrã de login.
const API = "";

export default function AdminCursoCheckinLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/api/checkin/auth/login`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Falha no login");
      }
      navigate("/admin/cursos/checkin", { replace: true });
    } catch (e) {
      setErr(e.message || "Erro");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0F1829] flex items-center justify-center p-4" data-testid="checkin-login">
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-white rounded-lg shadow-xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <ScanBarcode size={22} className="text-[#0F1829]" />
          <h1 className="text-base font-bold uppercase tracking-[0.12em] text-[#0F1829]">IHTP Check-in</h1>
        </div>
        <p className="text-xs text-[#6B7280] mb-5">Acesso restrito a operadores autorizados.</p>

        <label className="text-[11px] uppercase tracking-wider text-[#6B7280]">Email</label>
        <input
          type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="checkin-login-email"
          className="w-full mt-1 mb-3 px-3 py-2.5 text-base border border-[#E6E1D6] rounded focus:outline-none focus:ring-2 focus:ring-[#0F1829]/20" />

        <label className="text-[11px] uppercase tracking-wider text-[#6B7280]">Palavra-passe</label>
        <input
          type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          data-testid="checkin-login-password"
          className="w-full mt-1 mb-4 px-3 py-2.5 text-base border border-[#E6E1D6] rounded focus:outline-none focus:ring-2 focus:ring-[#0F1829]/20" />

        {err && <p className="text-xs text-[#9B1C1C] mb-3" data-testid="checkin-login-error">{err}</p>}

        <button type="submit" disabled={busy} data-testid="checkin-login-submit"
          className="w-full p-3 bg-[#0F1829] text-white rounded font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="animate-spin" size={14} />} Entrar
        </button>
      </form>
    </div>
  );
}
